import React, {useCallback, useEffect, useMemo, useState} from "react";
import useDiscoverSearchPoi from "../../hooks/useDiscoverSearchPoi";
import useTranslate from "../../hooks/useTranslate";
import {Badge, CloseIconButton, DestinationSelect, PoiSearchAutoComplete, PreLoading, SvgIcons} from "@tripian/react";
import Model, { helper } from "@tripian/model";
import useCities from "../../hooks/useCities";
import {useNavigate, useParams} from "react-router";
import moment from "moment/moment";
import {CITY_INFO, REGISTER, TRIPS, TOUR_DETAIL} from "../../constants/ROUTER_PATH_TITLE";
import { api } from "@tripian/core";
import AppNav from "../../App/AppNav/AppNav";
import Navbar from "./components/Navbar";
import DiscoverPageMain from "./components/DiscoverPageMain";
import FeaturedDestinations from "./components/FeaturedDestinations";
import FeaturedItineraries from "./components/FeaturedItineraries";
import Footer from "./components/Footer";
import {useLocalStorage} from "../../hooks/useLocalStorage";
import {useSelector} from "react-redux";
import ICombinedState from "../../redux/model/ICombinedState";
import PopularInterests from "./components/PopularInterests";
import InterestsPois from "./components/InterestsPois";
import RecommendTours from "./components/RecommendTours";
import TopAttractions from "./components/TopAttractions";
import SearchContainer from "../TripPlan/layouts/TripLayout/Container/SearchContainer/SearchContainer";
import usePoiCategory from "../../hooks/usePoiCategory";
import useDiscoverSearchAutoComplete from "../../hooks/useDiscoverSearchAutoComplete";
import useTourApi from "../../hooks/useTourApi";
import * as gtag from "../../gtag";

export type RSelectOption = {
    id: number;
    label: string;
    payload: {
        destinationId: number;
        destinationName: string;
        coordinate: Model.Coordinate;
        parentName: string;
    };
    isSelected?: boolean;
};

const DiscoverDetailPage = () => {
    const { cities, loadingCities } = useCities();
    const [loading, setLoading] = useState<boolean>(false);
    const [info, setInfo] = useState<{
        city: Model.City;
        details: Model.CityInfo;
        events: Model.CityEvent[];
    }>();
    const [error, setError] = useState<string>();
    const [showMore, setShowMore] = useState(false);
    const [selectedCategory, setSelectedCategory] = useState<string>();
    const [topTenPois, setTopTenPois] = useState<Model.Poi[] | undefined>(undefined);
    const { t, onSelectedLangCode, langCode } = useTranslate();
    const { cityId } = useParams<{ cityId: string }>();
    const { searchResults, loading: searchLoading, error: searchError, searchPoi } = useDiscoverSearchPoi(cityId ? parseInt(cityId) : undefined);
    const navigate = useNavigate();
    moment.locale();

    const { getLocalData } = useLocalStorage<"light" | "dark">("theme");
    const theme = getLocalData();


    document.title = CITY_INFO.TITLE(t("cityInfo.title"));

    const { poiCategoryGroups, loadingPoiCategories } = usePoiCategory();
    const [autoCompleteTags, setAutoCompleteTags] = useState<
        { value: string; label: string }[]
    >([]);

    const [autoCompleteLoading, setAutoCompleteLoading] =
        useState<boolean>(false);

    const { searchPoiAutoCompleteTags } = useDiscoverSearchAutoComplete(cityId ? parseInt(cityId) : undefined);
    const [selectedTags] = useState<string[]>([]);
    const [showFilters] = useState<boolean>(false);
    const [selectedPoiCategories, setSelectedPoiCategories] = useState<
        Model.PoiCategory[]
    >(
        poiCategoryGroups.flatMap(
            (categoryGroup: Model.PoiCategoryGroup) => categoryGroup.categories
        )
    );

    const handleCategorySelect = useCallback((mappedIds: number[], categoryName: string) => {
        setSelectedCategory(categoryName);
        // Set the selected categories in the filter system
        const categories = poiCategoryGroups
            .flatMap(group => group.categories)
            .filter(cat => mappedIds.includes(cat.id));
        setSelectedPoiCategories(categories);
    }, [poiCategoryGroups]);

    const handleCityInfoFetch = useCallback(async () => {
        if (!cityId || !cities) return;

        const selectedCityId = Number(cityId);
        setLoading(true);
        try {
            const [cityInfo, cityEvents] = await Promise.all([
                api.cityInfo(selectedCityId),
                api.cityEvents(selectedCityId)
            ]);

            const city = cities.find(city => city.id === selectedCityId);
            if (city) {
                setInfo({ city, details: cityInfo, events: cityEvents });
                setError(undefined);
            }
        } catch (error: any) {
            setInfo(undefined);
            setError(error);
        } finally {
            setLoading(false);
        }
    }, [cityId, cities]);

    useEffect(() => {
        if (cityId && cities) {
            handleCityInfoFetch();
        }
    }, [cities, cityId, handleCityInfoFetch]);

    // Fetch top ten POIs
    useEffect(() => {
        if (info?.city?.id) {
            api.topTen(info.city.id)
                .then(topTenData => {
                    if (topTenData.length > 0) setTopTenPois(topTenData[0].topTenPoi);
                    else setTopTenPois([]);
                })
                .catch(error => {
                    setTopTenPois([]);
                });
        }
    }, [info?.city?.id]);

    const isLoggedIn = useSelector(
        (state: ICombinedState) => state.user.isLoggedIn
    );

    const onClickSignUp = () => {
        if (isLoggedIn) {
            navigate(TRIPS.PATH);
        } else {
            if (window.tconfig.SHOW_REGISTER) {
                navigate(REGISTER.PATH);
            }
        }
    };

    const handleGoBack = () => {
        setInfo(undefined);
        navigate(-1);
    };

    const selectedPoiCategoryIds = useMemo(() => {
        const ids = selectedPoiCategories.map(cat => cat.id);
        return ids;
    }, [selectedPoiCategories]);

    useEffect(() => {
        setSelectedPoiCategories(
            poiCategoryGroups.flatMap(
                (categoryGroup: Model.PoiCategoryGroup) => categoryGroup.categories
            )
        );
    }, [poiCategoryGroups]);

    useEffect(() => {
        if (selectedPoiCategoryIds && selectedPoiCategoryIds.length > 0) {
            setAutoCompleteLoading(true);
            searchPoiAutoCompleteTags(selectedPoiCategoryIds)
                .then((options: { value: string; label: string }[]) => {
                    setAutoCompleteTags(options);
                })
                .catch(error => {
                    setAutoCompleteTags([]);
                })
                .finally(() => {
                    setAutoCompleteLoading(false);
                });
        } else {
            setAutoCompleteTags([]);
            setAutoCompleteLoading(false);
        }
    }, [searchPoiAutoCompleteTags, selectedPoiCategoryIds]);

    // Trigger search when filters or tags change
    useEffect(() => {
        if (selectedPoiCategoryIds.length > 0 || selectedTags.length > 0) {
            searchPoi({
                searchPoiCategories: selectedPoiCategoryIds,
                showOffersOnly: false,
                showBookingsOnly: false,
                searchText: selectedTags.length > 0 ? selectedTags.join('|') : undefined
            });
        }
    }, [selectedPoiCategoryIds, selectedTags, searchPoi]);

    const combinedQuery = useMemo(() => {
        if (selectedTags.length === 0) {
            return undefined;
        }
        return selectedTags;
    }, [selectedTags]);

    const filterButtonRef = React.useRef<HTMLButtonElement>(null);
    const [popupPosition, setPopupPosition] = useState<{
        top: number;
        right: number;
        placement: "top" | "bottom";
    }>({
        top: 0,
        right: 0,
        placement: "bottom",
    });

    useEffect(() => {
        if (showFilters && filterButtonRef.current) {
            const rect = filterButtonRef.current.getBoundingClientRect();
            const windowHeight = window.innerHeight;

            const estimatedPopupHeight = 400; // px
            const safetyMargin = 20; // px

            const spaceBelow = windowHeight - rect.bottom;
            const spaceAbove = rect.top;

            if (spaceBelow >= estimatedPopupHeight + safetyMargin) {
                setPopupPosition({
                    top: rect.bottom + 8,
                    right: window.innerWidth - rect.right,
                    placement: "bottom",
                });
            } else if (spaceAbove >= estimatedPopupHeight + safetyMargin) {
                setPopupPosition({
                    top: Math.max(100, rect.top - estimatedPopupHeight + 100),
                    right: window.innerWidth - rect.right,
                    placement: "top",
                });
            } else {
                if (spaceBelow >= spaceAbove) {
                    setPopupPosition({
                        top: rect.bottom + 8,
                        right: window.innerWidth - rect.right,
                        placement: "bottom",
                    });
                } else {
                    setPopupPosition({
                        top: Math.max(100, rect.top - estimatedPopupHeight + 100),
                        right: window.innerWidth - rect.right,
                        placement: "top",
                    });
                }
            }
        }
    }, [showFilters]);

    const mainCategoryGroups = useMemo(() => {
        return [
            { id: "all", name: t("trips.categories.all") },
            ...poiCategoryGroups.map(group => ({ id: group.name, name: group.name })),
        ];
    }, [poiCategoryGroups, t]);

    const sortedSelectedTags = useMemo(() => {
        const tagOrder = autoCompleteTags.map(tag => tag.value);
        return [...selectedTags].sort(
            (a, b) => tagOrder.indexOf(a) - tagOrder.indexOf(b)
        );
    }, [selectedTags, autoCompleteTags]);

    // Viator and Rezdy tours - memoize parameters to prevent re-fetching
    const tourParams = useMemo(() => {
        return {
            cityName: info?.city?.name,
            cityId: cityId || undefined,
            today: moment().format("YYYY-MM-DD"),
            futureDate: moment().add(29, "days").format("YYYY-MM-DD")
        };
    }, [info?.city?.name, cityId]); // Only recalculate when city changes

    const { tours: tourApiProducts, loading: loadingTourApi } = useTourApi({
        cityId: tourParams.cityId,
        providerId: String(Model.PROVIDER_ID.VIATOR),
        lat: info?.city?.coordinate?.lat,
        lng: info?.city?.coordinate?.lng,
        date: tourParams.today,
        to: tourParams.futureDate,
        adults: 2,
        currency: "usd",
        limit: 10,
        offset: 0,
        lang: "en",
        radius: 10,
    });

    const { tours: victoryApiProducts, loading: loadingVictoryApi } = useTourApi({
        cityId: tourParams.cityId,
        providerId: String(Model.PROVIDER_ID.VICTORY),
        lat: info?.city?.coordinate?.lat,
        lng: info?.city?.coordinate?.lng,
        date: tourParams.today,
        to: tourParams.futureDate,
        adults: 2,
        currency: "usd",
        limit: 10,
        offset: 0,
        lang: "en",
        radius: 10,
    });

    // Handle tour click - open tour detail page in new tab
    const handleTourClick = (
        provider: Model.PROVIDER_ID,
        tourId: string,
        additionalData?: any
    ) => {
        gtag.googleAnalyticsEvent("tour_modal_open", { provider, tour_id: tourId });
        // For all supported providers (Victory, Viator, etc.), open tour detail page in new tab
        const tourDetailPath = `${TOUR_DETAIL.PATH}?cityId=${cityId}&provider=${provider}&tourId=${tourId}`;
        window.open(tourDetailPath, '_blank');
    };

    return (
        <div>
            <Navbar
                theme={theme}
                t={t}
            />
            {loadingCities ? (
                <div className="flex items-center justify-center min-h-screen">
                    <PreLoading/>
                </div>
            ) : (
                <div className="min-h-screen bg-background-color">
                    <div className="w-full mx-auto">
                        {loading ? (
                            <div className="flex items-center justify-center min-h-screen">
                                <PreLoading/>
                            </div>
                        ) : (
                            <>
                                <DiscoverPageMain
                                    title={info?.city?.name || 'City'}
                                    description={info?.city?.description || ''}
                                />

                                {/* First RecommendTours - Victory Live Events */}
                                <RecommendTours
                                    tourApiProducts={victoryApiProducts}
                                    loadingTourApi={loadingVictoryApi}
                                    title={t("valai.todaysHotTickets")}
                                    provider="tourApi"
                                    onTourClick={handleTourClick}
                                    cityId={info?.city?.id}
                                    cityName={info?.city?.name}
                                />
                                <div className="max-w-7xl mx-auto pt-12 px-4 pb-3">
                                    <div>
                                        <div className="flex items-center justify-between">
                                            <h2 className="text-2xl font-semibold syne-fm">{t('valai.thingsTodo')}</h2>
                                            <button
                                                className="text-primary-color font-medium text-base"
                                                onClick={() => {
                                                    window.scrollTo(0, 0);
                                                    navigate(`/things-to-do/${cityId}`);
                                                }}
                                            >
                                                View All
                                            </button>
                                        </div>

                                    </div>
                                </div>
                                {/* Second RecommendTours - Viator with "Tours & Tickets" */}

                                <InterestsPois
                                    searchResults={searchResults || []}
                                    loading={searchLoading}
                                    title={selectedCategory || t("valai.topAttractions")}
                                    t={t}
                                />
                                <RecommendTours
                                    tourApiProducts={tourApiProducts}
                                    loadingTourApi={loadingTourApi}
                                    title={t("valai.tourTickets")}
                                    provider="tourApi"
                                    onTourClick={handleTourClick}
                                    cityId={info?.city?.id}
                                    cityName={info?.city?.name}
                                />
                                {info && topTenPois && topTenPois.length > 0 && (
                                    <>
                                        <TopAttractions
                                            cityName={info.city.name}
                                            attractions={topTenPois}
                                            t={t}
                                        />
                                        <FeaturedDestinations />
                                        <Footer t={t} />
                                    </>
                                )}
                            </>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default DiscoverDetailPage;